import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { Holiday2021Service } from '../../holiday-2021/holiday-2021.service';
import { PreciseDiff, preciseDifference } from '../../utilities/preciseDiff';

@Component({
  selector: 'lohi-holiday-twentyone-countdown',
  templateUrl: './holiday-twentyone-countdown.component.html',
  styles: [
    `
      .clock-number {
        height: 95px;
        min-width: 80px;
        background: url('/assets/images/2021_holiday_clock_bg.svg');
        background-size: cover;
        line-height: 95px;
        text-align: center;
        font-size: 6rem;
      }

      @media only screen and (max-width: 1023px) {
        .clock-number {
          height: 48px;
          min-width: 40px;
          background: url('/assets/images/2021_holiday_clock_bg.svg');
          background-size: contain;
          line-height: 48px;
          text-align: center;
          font-size: 3rem;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HolidayTwentyoneCountdownComponent implements OnInit {
  public registrationEndsAt$: Observable<Date | null>;
  public preciseDiff$: Observable<PreciseDiff>;
  public dayDigits$: Observable<{ tens: number; ones: number }>;
  public hourDigits$: Observable<{ tens: number; ones: number }>;
  public minuteDigits$: Observable<{ tens: number; ones: number }>;
  public secondDigits$: Observable<{ tens: number; ones: number }>;

  constructor(private service: Holiday2021Service) {}

  public ngOnInit(): void {
    this.registrationEndsAt$ = this.service.registrationEndsAt$;
    this.preciseDiff$ = this.registrationEndsAt$.pipe(
      switchMap((date) => {
        if (!date) {
          return of(null);
        }
        return timer(0, 1000).pipe(
          map(() => {
            return preciseDifference(date, new Date(), ['day', 'hour', 'minute', 'second']);
          }),
        );
      }),
      shareReplay(1),
    );
    this.dayDigits$ = this.preciseDiff$.pipe(
      map((pd) => {
        return tensAndOnes(pd?.days);
      }),
    );
    this.hourDigits$ = this.preciseDiff$.pipe(
      map((pd) => {
        return tensAndOnes(pd?.hours);
      }),
    );
    this.minuteDigits$ = this.preciseDiff$.pipe(
      map((pd) => {
        return tensAndOnes(pd?.minutes);
      }),
    );
    this.secondDigits$ = this.preciseDiff$.pipe(
      map((pd) => {
        return tensAndOnes(pd?.seconds);
      }),
    );
  }
}

function tensAndOnes(n: number): { tens: number; ones: number } {
  if (!n) {
    return {
      tens: 0,
      ones: 0,
    };
  }
  return {
    tens: Math.floor(n / 10),
    ones: n % 10,
  };
}
